'use client';

import type { RegisterFileBasicItem } from '@/lib/backend-api';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { WEB_CREDIT_COSTS } from '@/config/credit-costs.config';
import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';

interface RegisterDocActionsProps {
  isAuthenticated: boolean;
  canDownload: boolean;
  openRegisterFile: (fileType: string, refresh: boolean, documentId?: number, docDate?: string | null) => Promise<void>;
  file: RegisterFileBasicItem;
  t: TFunction;
  isDeleted: boolean;
  loadingFileTypes: Set<string>;
  downloadingFileTypes: Set<string>;
  unavailableFileTypes: Set<string>;
  isAutoUpdating: boolean;
  setSignupDialogOpen: Dispatch<SetStateAction<boolean>>;
}

export function RegisterDocActions({
  isAuthenticated,
  canDownload,
  openRegisterFile,
  file,
  t,
  isDeleted,
  loadingFileTypes,
  downloadingFileTypes,
  unavailableFileTypes,
  isAutoUpdating,
  setSignupDialogOpen,
}: RegisterDocActionsProps) {
  return (
    <div className="flex flex-col items-stretch gap-1 shrink-0 w-[110px]">
      {/* Anzeigen button: shown when file exists, stays clickable
        even during refresh and after a failed refresh.
        Always downloads the cached file; retries happen via the
        inline refresh icon next to the date. */}
      {isAuthenticated && canDownload && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 border-0 lg:border bg-transparent lg:bg-background rounded-full shadow-none"
                onClick={() =>
                  openRegisterFile(
                    file.file_type,
                    false,
                    undefined,
                    file.doc_date ?? file.published_at,
                  )
                }
              >
                <Download className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                {t('companyDetail.registerDocs.view', 'View')}
              </Button>
            </TooltipTrigger>
            {/* Price on hover/focus, before the click. */}
            <TooltipContent>
              {t('credits.costTooltip', {
                n: WEB_CREDIT_COSTS.registerFileDownload,
                defaultValue:
                  'Costs {{n}} credits — once you have paid for a document it stays free.',
              })}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {/* Abrufen button when no file exists yet — clickable
        in the idle state, including after a failed refresh so
        the user can retry. */}
      {isAuthenticated &&
        !isDeleted &&
        !loadingFileTypes.has(file.file_type) &&
        !downloadingFileTypes.has(file.file_type) &&
        !canDownload &&
        !unavailableFileTypes.has(file.file_type) &&
        !isAutoUpdating && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 border-0 lg:border bg-transparent lg:bg-background rounded-full shadow-none"
                  onClick={() =>
                    openRegisterFile(
                      file.file_type,
                      true,
                      undefined,
                      file.doc_date ?? file.published_at,
                    )
                  }
                >
                  <RefreshCw className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                  {t(
                    'companyDetail.registerDocs.fetch',
                    'Fetch',
                  )}
                </Button>
              </TooltipTrigger>
              {/* Refresh is a live registry scrape — always
                billed, so no "stays free" clause here. */}
              <TooltipContent>
                {t('credits.costTooltipRefresh', {
                  n: WEB_CREDIT_COSTS.registerFileDownload,
                  defaultValue:
                    'Costs {{n}} credits — a manual update fetches a fresh copy.',
                })}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      {/* Abrufen disabled with spinner during refresh
        (manual click or auto-update) so the slot stays filled. */}
      {isAuthenticated &&
        !isDeleted &&
        !downloadingFileTypes.has(file.file_type) &&
        !canDownload &&
        (loadingFileTypes.has(file.file_type) ||
          isAutoUpdating) && (
          <Button
            variant="outline"
            size="sm"
            className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 border-0 lg:border bg-transparent lg:bg-background rounded-full shadow-none"
            disabled
          >
            <Loader2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 animate-spin mr-1" />
            {t('companyDetail.registerDocs.fetch', 'Fetch')}
          </Button>
        )}
      {!isAuthenticated && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 whitespace-nowrap border-0 lg:border bg-transparent lg:bg-background rounded-full shadow-none"
                onClick={() => setSignupDialogOpen(true)}
              >
                <Download className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                {t(
                  'companyDetail.registerDocs.signup',
                  'Sign in',
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {t('companyDetail.registerDocs.loginToDownload')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
